import { memo } from 'react';

interface SkeletonProps {
  className?: string;
}

/**
 * Componente base de skeleton para loading states
 */
export const Skeleton = memo(function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
  );
});

/**
 * Skeleton para una tarjeta de producto
 */
export const ProductCardSkeleton = memo(function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-sm">
      <div className="h-48 bg-gray-200 animate-pulse" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
        <div className="flex justify-between items-center">
          <div className="h-6 bg-gray-200 rounded w-20 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-12 animate-pulse" />
        </div>
        <div className="h-10 bg-gray-200 rounded animate-pulse" />
      </div>
    </div>
  );
});

/**
 * Skeleton para grid de productos
 */
interface ProductGridSkeletonProps {
  count?: number;
  columns?: 2 | 3 | 4 | 5;
}

export const ProductGridSkeleton = memo(function ProductGridSkeleton({
  count = 8,
  columns = 4
}: ProductGridSkeletonProps) {
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    5: 'grid-cols-2 md:grid-cols-5'
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-4`}>
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
});

/**
 * Skeleton para producto destacado (featured)
 */
export const FeaturedProductSkeleton = memo(function FeaturedProductSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
      <div className="aspect-[4/3] bg-gray-200 animate-pulse" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
        <div className="bg-gray-100 p-3 rounded-lg">
          <div className="flex justify-between">
            <div className="h-6 bg-gray-200 rounded w-20 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-8 animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
        </div>
      </div>
    </div>
  );
});

/**
 * Skeleton para sección de productos destacados
 */
interface FeaturedSectionSkeletonProps {
  count?: number;
}

export const FeaturedSectionSkeleton = memo(function FeaturedSectionSkeleton({
  count = 3
}: FeaturedSectionSkeletonProps) {
  return (
    <section>
      <div className="animate-pulse space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-10 bg-gray-200 rounded w-1/3" />
          <div className="h-8 bg-gray-200 rounded w-24" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: count }).map((_, i) => (
            <FeaturedProductSkeleton key={i} />
          ))}
        </div>
      </div>
    </section>
  );
});

/**
 * Skeleton para producto compacto (new products grid)
 */
export const CompactProductSkeleton = memo(function CompactProductSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
      <div className="aspect-square bg-gray-200 animate-pulse" />
      <div className="p-3 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
        <div className="bg-gray-100 p-2 rounded-lg">
          <div className="flex justify-between">
            <div className="h-5 bg-gray-200 rounded w-16 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-8 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Skeleton para sección de nuevos productos
 */
interface NewProductsSectionSkeletonProps {
  count?: number;
}

export const NewProductsSectionSkeleton = memo(function NewProductsSectionSkeleton({
  count = 5
}: NewProductsSectionSkeletonProps) {
  return (
    <section>
      <div className="animate-pulse space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-200 rounded-full" />
            <div className="h-8 bg-gray-200 rounded w-40" />
          </div>
          <div className="h-10 bg-gray-200 rounded-lg w-24" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: count }).map((_, i) => (
            <CompactProductSkeleton key={i} />
          ))}
        </div>
      </div>
    </section>
  );
});

/**
 * Skeleton para productos relacionados
 */
interface RelatedProductsSkeletonProps {
  count?: number;
  title?: string;
}

export const RelatedProductsSkeleton = memo(function RelatedProductsSkeleton({
  count = 8,
  title = "Productos Relacionados"
}: RelatedProductsSkeletonProps) {
  return (
    <div className="py-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-200 h-32 rounded-lg mb-2" />
            <div className="bg-gray-200 h-4 rounded mb-1" />
            <div className="bg-gray-200 h-3 rounded w-2/3" />
          </div>
        ))}
      </div>
    </div>
  );
});

/**
 * Skeleton para página de detalles de producto
 */
export const ProductDetailsSkeleton = memo(function ProductDetailsSkeleton() {
  return (
    <div className="bg-white min-h-screen">
      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Gallery */}
          <div className="space-y-6">
            <div className="relative rounded-xl overflow-hidden bg-gray-200 aspect-square animate-pulse" />
            <div className="grid grid-cols-6 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-lg" />
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <Skeleton className="h-8 w-3/4 mb-4" />
              <Skeleton className="h-6 w-1/2 mb-4" />
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-4 mb-4">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-32 rounded-full" />
            </div>

            {/* Description box */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </div>
            </div>

            {/* Price section */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-100">
              <div className="flex items-baseline gap-2 mb-4">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-6 w-20" />
              </div>
            </div>

            {/* Quantity and button */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
                <div className="w-full sm:w-1/3">
                  <Skeleton className="h-4 w-16 mb-2" />
                  <Skeleton className="h-10 w-full rounded-lg" />
                </div>
                <div className="w-full sm:w-2/3">
                  <Skeleton className="h-12 w-full rounded-lg" />
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6 my-4 border-t border-b">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <Skeleton className="w-6 h-6 rounded" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-20 mb-1" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>

            {/* WhatsApp button */}
            <Skeleton className="h-12 w-full rounded-lg" />
          </div>
        </div>

        {/* Description and specs section */}
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-5 w-24" />
              </div>
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </div>
            </div>
          </div>
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <Skeleton className="h-6 w-40 mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg">
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Related products */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                <Skeleton className="aspect-square" />
                <div className="p-4 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-5 w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Skeleton;
